import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowRight, Eye, EyeOff, ShieldCheck } from 'lucide-react';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const form = new FormData(e.target);
    try {
      const user = await login(form.get('login'), form.get('password'));
      const target = location.state?.from;
      if (target && typeof target === 'string' && target.startsWith('/')) {
        navigate(target, { replace: true });
      } else {
        navigate(user?.role === 'field_staff' ? '/staff' : '/', { replace: true });
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen overflow-hidden bg-white text-zinc-950" data-testid="login-page">
      <div className="grid min-h-screen lg:grid-cols-[0.9fr_1.1fr]" data-testid="login-shell">
        <section className="relative hidden min-h-screen flex-col justify-between overflow-hidden border-r-2 border-zinc-950 bg-zinc-950 p-10 text-white lg:flex" data-testid="login-brand-panel">
          <img src="https://images.pexels.com/photos/34764468/pexels-photo-34764468.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1000" alt="Minimal service operations backdrop" className="absolute inset-0 h-full w-full object-cover opacity-40 grayscale" data-testid="login-background-image" />
          <div className="relative z-10 flex items-center gap-4">
            <div className="inline-flex h-20 w-20 items-center justify-center border-2 border-white bg-white" data-testid="login-logo-frame">
              <img src="/bcjr-logo.png" alt="Butler County Junk Removal" className="h-16 w-16 object-contain" data-testid="login-logo" />
            </div>
            <div>
              <p className="font-mono text-xs font-black uppercase tracking-[0.22em] text-[#E4FF00]" data-testid="login-eyebrow">FieldCRM</p>
              <h1 className="font-heading text-3xl font-black tracking-tight text-white" data-testid="login-title">
                BCJR
              </h1>
            </div>
          </div>

          <div className="relative z-10 max-w-lg">
            <p className="font-mono text-xs font-black uppercase tracking-[0.22em] text-[#E4FF00]" data-testid="login-heading-kicker">Operator access</p>
            <h2 className="mt-4 font-heading text-5xl font-black leading-none tracking-tight text-white" data-testid="login-heading">
              One clean desk for the day’s work.
            </h2>
            <p className="mt-5 max-w-md text-base font-semibold leading-7 text-zinc-100" data-testid="login-subtitle">
              Leads, schedules, invoices, messages, and crew work stay in one secure operating system.
            </p>
          </div>

          <div className="relative z-10 flex flex-wrap gap-3 text-xs font-black uppercase tracking-[0.12em] text-zinc-950" data-testid="login-trust-signals">
            {['Secure', 'Mobile ready', 'Multi-DBA'].map((item) => (
              <div key={item} className="flex min-h-11 items-center gap-2 bg-[#E4FF00] px-4" data-testid={`login-trust-${item.toLowerCase().replaceAll(' ', '-')}`}>
                <ShieldCheck className="h-4 w-4" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="flex min-h-screen items-center justify-center px-5 py-8 sm:px-10 lg:px-20" data-testid="login-form-panel">
          <div className="w-full max-w-xl">
            <div className="mb-12 flex items-center gap-4 lg:hidden" data-testid="login-mobile-brand">
              <div className="inline-flex h-20 w-20 items-center justify-center border-2 border-zinc-950 bg-white" data-testid="login-mobile-logo-frame">
                <img src="/bcjr-logo.png" alt="Butler County Junk Removal" className="h-16 w-16 object-contain" data-testid="login-mobile-logo" />
              </div>
              <div>
                <p className="font-mono text-xs font-black uppercase tracking-[0.22em] text-zinc-500">FieldCRM</p>
                <h1 className="font-heading text-2xl font-black tracking-tight text-zinc-950">BCJR</h1>
              </div>
            </div>

            <div className="mb-10">
              <p className="font-mono text-xs font-black uppercase tracking-[0.22em] text-zinc-500" data-testid="login-form-eyebrow">Secure login</p>
              <h2 className="mt-3 font-heading text-5xl font-black leading-none tracking-tight text-zinc-950 sm:text-6xl" data-testid="login-form-title">Sign in.</h2>
              <p className="mt-4 max-w-md text-base font-semibold leading-7 text-zinc-600" data-testid="login-form-subtitle">Access the high-visibility command center for today’s jobs.</p>
            </div>
            {error && (
              <div className="mb-6 border-l-4 border-red-600 bg-red-50 p-4 text-base font-black text-red-800" data-testid="auth-error">
                {error}
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-6" data-testid="login-form">
              <div>
                <Label htmlFor="login-identifier" className="text-xs font-black uppercase tracking-[0.16em] text-zinc-700">Email or username</Label>
                <Input
                  id="login-identifier"
                  name="login"
                  type="text"
                  required
                  placeholder="Enter email or username"
                  autoComplete="username"
                  className="mt-2 h-16 rounded-none border-2 border-zinc-950 bg-white px-4 text-lg font-black shadow-[4px_4px_0px_0px_rgba(24,24,27,1)] transition-[box-shadow,transform] duration-150 focus-visible:border-zinc-950 focus-visible:ring-4 focus-visible:ring-[#E4FF00]"
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <Label htmlFor="login-password" className="text-xs font-black uppercase tracking-[0.16em] text-zinc-700">Password</Label>
                <div className="relative">
                  <Input
                    id="login-password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    placeholder="Enter password"
                    className="mt-2 h-16 rounded-none border-2 border-zinc-950 bg-white px-4 pr-16 text-lg font-black shadow-[4px_4px_0px_0px_rgba(24,24,27,1)] transition-[box-shadow,transform] duration-150 focus-visible:border-zinc-950 focus-visible:ring-4 focus-visible:ring-[#E4FF00]"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-4 z-30 inline-flex h-12 w-12 items-center justify-center border-2 border-zinc-950 bg-white text-zinc-950 transition-[background-color,transform] duration-150 hover:bg-[#E4FF00] active:scale-95"
                    onClick={() => setShowPassword(prev => !prev)}
                    aria-pressed={showPassword}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    data-testid="login-password-toggle"
                  >
                    {showPassword ? <EyeOff className="h-6 w-6" /> : <Eye className="h-6 w-6" />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="h-16 w-full rounded-none border-2 border-zinc-950 bg-zinc-950 text-lg font-black text-white shadow-[4px_4px_0px_0px_rgba(228,255,0,1)] transition-[background-color,color,transform,box-shadow] duration-150 hover:bg-[#E4FF00] hover:text-zinc-950 active:translate-x-1 active:translate-y-1 active:shadow-none" disabled={loading} data-testid="login-submit-button">
                {loading ? 'Signing in...' : <><span>Sign in</span><ArrowRight className="h-5 w-5" /></>}
              </Button>
            </form>
            <p className="mt-8 border-t-2 border-zinc-950 pt-5 text-sm font-bold text-zinc-500" data-testid="login-security-note">Authorized operators only.</p>
          </div>
        </section>
      </div>
    </div>
  );
}
